import React, { useState } from 'react';
import { Modal, Tag, Typography, Alert, Button, Spin } from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, SafetyOutlined,
    PercentageOutlined, GlobalOutlined, ControlOutlined, BankOutlined
} from '@ant-design/icons';
import axios from 'axios';
import Datas from "../Data/Data.json";
import { Crypter } from '../Functions/Security/Crypter';
import { useAuthHeader, useAuthUser } from '../Services/Auth/sessionAuth';

const { Title, Text } = Typography;

const fmtNum = (v: any) =>
    (v === null || v === undefined || v === '') ? '—' : Number(v).toLocaleString('fr-FR');

const fmtPct = (v: any) =>
    (v === null || v === undefined || v === '') ? '—' : `${Number(v).toLocaleString('fr-FR')} %`;

const fmtMoney = (v: any) =>
    (v === null || v === undefined || v === '') ? '—' : `${Number(v).toLocaleString('fr-FR')} FCFA`;

const flag = (v: any) => v === true || v === 1;

const FlagTag = ({ label, value }: { label: string, value: any }) => (
    <Tag
        icon={flag(value) ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        color={flag(value) ? 'success' : 'default'}
        style={{ marginBottom: 6, fontSize: 12 }}
    >
        {label}
    </Tag>
);

const Section = ({ icon, title, children }: { icon: any, title: string, children: any }) => (
    <div style={{ marginBottom: 18 }}>
        <Title level={5} style={{ color: '#116599', marginBottom: 8 }}>
            {icon} {title}
        </Title>
        {children}
    </div>
);

const Item = ({ label, value }: { label: string, value: any }) => (
    <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '7px 10px',
        borderBottom: '1px solid #f0f0f0',
        background: '#fafafa',
        borderRadius: 4,
        marginBottom: 4,
        minWidth: 0,
    }}>
        <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>{label}</Text>
        <Text strong style={{ fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value}
        </Text>
    </div>
);

const Grid = ({ children }: { children: any }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 16 }}>
        {children}
    </div>
);

export const TypePretInfoModal = (props: { open: boolean, data: any, loading?: boolean, onClose: () => void }) => {
    const tp = props.data;

    return (
        <Modal
            open={props.open}
            onCancel={props.onClose}
            width={780}
            centered
            title={
                <div>
                    <Title level={4} style={{ margin: 0, color: '#116599' }}>
                        <BankOutlined /> Type de prêt : {tp?.TYPEPRET ?? ''}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {tp?.LIBELLE ?? ''} — configuration indicative du produit
                    </Text>
                </div>
            }
            footer={<Button type="primary" onClick={props.onClose}>Compris</Button>}
        >
            <Spin spinning={!!props.loading}>
                {tp && (
                    <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 8 }}>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Ces paramètres sont définis au niveau du type de prêt et servent de contrôle à la saisie."
                        />

                        <Section icon={<ControlOutlined />} title="Limites de saisie">
                            <Grid>
                                <Item label="Montant max autorisé" value={fmtMoney(tp.mntMaxAuto)} />
                                <Item label="Nombre d'échéances max" value={fmtNum(tp.nbrEchMax)} />
                                <Item label="Taux d'intérêt minimum" value={fmtPct(tp.tauxintmin)} />
                                <Item label="Écart échéance / pénalité" value={`${fmtNum(tp.ECARTECHPEN)} j`} />
                                <Item label="Jours avant impayé" value={`${fmtNum(tp.NBJAVTIMP)} j`} />
                                <Item label="Constat impayé" value={`${fmtNum(tp.JrConstatImp)} j`} />
                                <Item label="Constat créance douteuse" value={`${fmtNum(tp.JrConstatDouteux)} j`} />
                                <Item label="Plafond NDR" value={fmtMoney(tp.PLAFONDNDR)} />
                            </Grid>
                        </Section>

                        <Section icon={<PercentageOutlined />} title="Taux et fiscalité">
                            <Grid>
                                <Item label="Taux pénalité" value={fmtPct(tp.TauxPenalite)} />
                                <Item label="Taux taxe payée" value={fmtPct(tp.TAUXTAX)} />
                                <Item label="Taux TVA impayé" value={fmtPct(tp.TVAIMPAIE)} />
                                <Item label="Montant min taxable" value={fmtMoney(tp.MTNMINTAXABLE)} />
                                <Item label="Taux provision" value={fmtPct(tp.txtProv)} />
                                <Item label="Taux quotidité" value={fmtPct(tp.TAUXQUODITE)} />
                                <Item label="Taux épargne" value={fmtPct(tp.TAUXEPARG)} />
                                <Item label="Montant épargne" value={fmtMoney(tp.MONTEPARG)} />
                            </Grid>
                        </Section>

                        <Section icon={<SafetyOutlined />} title="Options du produit">
                            <FlagTag label="Gestion impayés" value={tp.GESIMPAI} />
                            <FlagTag label="Créances douteuses" value={tp.isGestCrDout} />
                            <FlagTag label="Quotidité" value={tp.ISQUODITE} />
                            <FlagTag label="Paiement épargne" value={tp.ISPAIEPARG} />
                            <FlagTag label="Blocage épargne" value={tp.isbloqeparg} />
                            <FlagTag label="Remb. compte groupe" value={tp.isRembCptgrp} />
                            <FlagTag label="Frais service amort." value={tp.ISFRAISSERVAMORT} />
                            <FlagTag label="Gestion assurance" value={tp.isGestAssur} />
                            <FlagTag label="Paiement taxe" value={tp.PAYEMTAX} />
                            <FlagTag label="TVA impayée" value={tp.ISTVAIMPAIE} />
                            <FlagTag label="Modif. taux autorisée" value={tp.isModifTxInt} />
                            <FlagTag label="Modif. nb échéances" value={tp.isModifNbrEch} />
                            <FlagTag label="Modif. date échéance" value={tp.isModifDatEch} />
                            <FlagTag label="Personne liée" value={tp.PERSLIEE} />
                            <FlagTag label="Crédit restructuré" value={tp.RESTRUCTURE} />
                            <div style={{ marginTop: 6 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Base pénalité : {flag(tp.ISRESDU) ? 'restant dû' : 'montant échéance'}
                                </Text>
                            </div>
                        </Section>

                        <Section icon={<GlobalOutlined />} title="Classification réglementaire (COBAC / SESSEM)">
                            <Grid>
                                <Item label="Catégorie COBAC" value={tp._TERME?.CATEGCOBAC ?? tp.CATEGCOBAC ?? '—'} />
                                <Item label="Terme (durée)" value={tp._TERME ? `${tp.DUREE ?? ''} — ${tp._TERME.LIBELLE ?? ''} · ${tp._TERME.DUREEMIN ?? 0} – ${tp._TERME.DUREEMAX ?? '∞'} mois · ≤ ${tp._TERME.NBRECHMAX ?? '∞'} éch.` : (tp.DUREE ?? '—')} />
                                <Item
                                    label="Bénéficiaires autorisés"
                                    value={
                                        tp.BENEFICIAIRE
                                            ? <span>{String(tp.BENEFICIAIRE).split(',').map((c: string) => <Tag key={c} color="blue">{c.trim()}</Tag>)}</span>
                                            : '—'
                                    }
                                />
                                <Item label="Plafond NDR" value={fmtMoney(tp.PLAFONDNDR)} />
                            </Grid>
                        </Section>
                    </div>
                )}
            </Spin>
        </Modal>
    );
};

/**
 * Charge la configuration d'un type de prêt (READ) et ouvre la modale d'information.
 * Usage :
 *   const { typePret, loadTypePret, typePretModal } = useTypePret();
 *   <SearchModal name='TYPEPRET' handleFunction={loadTypePret} ... />
 *   {typePretModal}
 */
export const useTypePret = () => {
    const auth = useAuthUser();
    const authHeader = useAuthHeader();
    const [typePret, setTypePret] = useState<any>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Charge le Terme (code DUREE) associe au type de pret -> typePret._TERME
    const loadTerme = (tp: any) => {
        const codage = auth()?.codage ?? '';
        if (!tp?.DUREE || !Datas.ApiUrl.ReadTerme) { setTypePret(tp); return; }
        axios({
            method: 'get',
            maxBodyLength: Infinity,
            url: Datas.ApiUrl.ReadTerme,
            headers: {
                'CypherTerme': tp.DUREE + '|' + codage,
                'Authorization': authHeader(),
                'Content-Type': 'application/json'
            },
        }).then((response) => {
            if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data));
                if (res.StatutCode == 200 && res.Result?.Table?.length) {
                    setTypePret({ ...tp, _TERME: res.Result.Table[0] });
                    return;
                }
            }
            setTypePret(tp);
        }).catch(() => setTypePret(tp));
    };

    const loadTypePret = (code: string) => {
        if (!code) {
            setTypePret(null);
            return;
        }
        setLoading(true);
        axios({
            method: 'get',
            maxBodyLength: Infinity,
            url: Datas.ApiUrl.ReadTypepret,
            headers: {
                'CypherTypepret': code,
                'Authorization': authHeader(),
                'Content-Type': 'application/json'
            },
        }).then((response) => {
            setLoading(false);
            if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data));
                if (res.StatutCode == 200 && res.Result?.Table?.length) {
                    loadTerme(res.Result.Table[0]);
                    setOpen(true);
                } else {
                    setTypePret(null);
                }
            }
        }).catch((error) => {
            setLoading(false);
            setTypePret(null);
            console.log(error);
        });
    };

    const typePretModal = (
        <TypePretInfoModal
            open={open}
            loading={loading}
            data={typePret}
            onClose={() => setOpen(false)}
        />
    );

    return { typePret, loadTypePret, typePretModal };
};

/** Correspondance codes REF_BENEFICIAIRE -> CLIENT.TYPCLI (P=physique, M=morale, I=interne). */
export const BENEF_TYPCLI: Record<string, string[]> = {
    PARTICULIER: ['P'],
    PERSONNEL: ['P', 'I'],
    ENTREPRISE: ['M'],
    ADMINISTRATION: ['M'],
    MICROFINANCE: ['M'],
    LIEE: ['P', 'M'],
};

/** Vrai si le type client TYPCLI est couvert par au moins un code beneficiaire autorise. */
export const isBeneficiaireAllowed = (beneficiaire: any, typcli: any): boolean => {
    const codes = String(beneficiaire ?? '').split(',').map(s => s.trim()).filter(Boolean);
    const t = String(typcli ?? '').trim().toUpperCase();
    if (!codes.length || !t) return true;
    return codes.some(c => (BENEF_TYPCLI[c] ?? ['P', 'M', 'I']).includes(t));
};

/** Charge la fiche client (GetClient) et verifie son type contre la liste des beneficiaires. */
export const checkClientBeneficiaire = async (authHeader: () => string, numcli: string, beneficiaire: any): Promise<boolean> => {
    const codes = String(beneficiaire ?? '').split(',').map(s => s.trim()).filter(Boolean);
    if (!codes.length || !numcli) return true;
    try {
        const response = await axios({
            method: 'get',
            maxBodyLength: Infinity,
            url: Datas.ApiUrl.GetClient,
            headers: {
                'CypherNumcli': Crypter.encrypt(numcli),
                'Authorization': authHeader(),
                'Content-Type': 'application/json'
            },
        });
        if (response.data) {
            const res = JSON.parse(Crypter.decrypt(response.data));
            const cli = res.Result?.Table?.[0];
            if (cli) return isBeneficiaireAllowed(beneficiaire, cli.TYPCLI);
        }
    } catch (e) {
        console.log(e);
    }
    return true;
};

/** Nombre de mois couverts par une echeance selon la periodicite (codes v_period_lib). */
export const PERIOD_MONTHS: Record<string, number> = {
    J: 1 / 30, H: 1 / 4, F: 0.5, M: 1, B: 2, T: 3, S: 6, A: 12
};

const DUREE_BOUNDS: Record<string, [number, number, string]> = {
    CT: [0, 12, 'court terme (≤ 12 mois)'],
    MT: [12, 60, 'moyen terme (12 – 60 mois)'],
    LT: [60, Infinity, 'long terme (> 60 mois)'],
};

/** Regle antd : coherence NBRECHE x PERIODIC avec la categorie de duree.
 *  Si `bounds` est fourni (ex. [DUREEMIN, DUREEMAX, libelle] du TERME), il a priorite
 *  sur le mapping historique CT/MT/LT. */
export const ruleDuree = (duree: string, getPeriodic: () => any, toNum: (v: any) => number | null, bounds?: [number, number, string]) => ({
    validator: (_: any, value: any) => {
        const b = bounds ?? DUREE_BOUNDS[duree];
        if (!b) return Promise.resolve();
        const n = toNum(value);
        const per = String(getPeriodic() ?? '').trim().toUpperCase();
        const months = n !== null && PERIOD_MONTHS[per] ? n * PERIOD_MONTHS[per] : null;
        if (months === null || (months >= b[0] && months <= b[1]))
            return Promise.resolve();
        return Promise.reject(new Error(`Durée incompatible : ce type de prêt est ${b[2]}`));
    }
});

/** Bornes de duree issues du TERME charge (typePret._TERME). */
export const termeBounds = (terme: any): [number, number, string] | undefined => {
    if (!terme) return undefined;
    const min = Number(terme.DUREEMIN) || 0;
    const max = Number(terme.DUREEMAX);
    const maxB = max > 0 ? max : Infinity;
    return [min, maxB, `${terme.LIBELLE ?? 'terme'} (${min} – ${max > 0 ? max : '∞'} mois)`];
};

/** Normalise une valeur de champ (string "1 500,5" / number) en nombre. */
export const toNumField = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
    return isNaN(n) ? null : n;
};

/** Règle antd : valeur maximale autorisée. */
export const ruleMaxValue = (max: number, label: string) => ({
    validator: (_: any, value: any) => {
        const n = toNumField(value);
        if (n !== null && n > max)
            return Promise.reject(new Error(`${label} ne doit pas dépasser ${Number(max).toLocaleString('fr-FR')}`));
        return Promise.resolve();
    }
});

/** Règle antd : valeur minimale autorisée. */
export const ruleMinValue = (min: number, label: string) => ({
    validator: (_: any, value: any) => {
        const n = toNumField(value);
        if (n !== null && n < min)
            return Promise.reject(new Error(`${label} ne doit pas être inférieur à ${Number(min).toLocaleString('fr-FR')}`));
        return Promise.resolve();
    }
});

export default TypePretInfoModal;
